import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { DrugSynonym } from '../../../application/types/drug';

interface SynonymDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: {
        barCode?: string;
        gtin?: string;
        produktName?: string;
    }) => Promise<void>;
    synonym?: DrugSynonym | null;
    isLoading?: boolean;
}

const SynonymDialog: React.FC<SynonymDialogProps> = ({
    open,
    onClose,
    onSave,
    synonym,
    isLoading = false,
}) => {
    const [barCode, setBarCode] = useState('');
    const [gtin, setGtin] = useState('');
    const [produktName, setProduktName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const isEdit = !!synonym;

    useEffect(() => {
        if (open) {
            if (synonym) {
                setBarCode(synonym.barCode || '');
                setGtin(synonym.gtin || '');
                setProduktName(synonym.produktName || '');
            } else {
                setBarCode('');
                setGtin('');
                setProduktName('');
            }
            setError(null);
        }
    }, [open, synonym]);

    const handleSave = async () => {
        // Validation: at least one of barCode or gtin is required
        if (!barCode.trim() && !gtin.trim()) {
            setError('Antingen streckkod eller GTIN måste anges');
            return;
        }

        setError(null);
        setSaving(true);

        try {
            await onSave({
                barCode: barCode.trim() || undefined,
                gtin: gtin.trim() || undefined,
                produktName: produktName.trim() || undefined,
            });
            onClose();
        } catch (err: any) {
            // Handle specific error messages from backend
            const responseData = err.response?.data;
            let errorMessage = 'Ett fel uppstod';

            if (typeof responseData === 'string') {
                errorMessage = responseData;
            } else if (responseData?.exception) {
                errorMessage = responseData.exception;
            } else if (responseData?.messages?.length > 0) {
                errorMessage = responseData.messages.join(', ');
            } else if (responseData?.message) {
                errorMessage = responseData.message;
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEdit
                    ? 'Redigera synonympreparat'
                    : 'Lägg till synonympreparat'}
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        label="Varunamn"
                        value={produktName}
                        onChange={(e) => setProduktName(e.target.value)}
                        fullWidth
                        helperText="Namn på synonympreparat"
                    />

                    <TextField
                        label="Streckkod"
                        value={barCode}
                        onChange={(e) => setBarCode(e.target.value)}
                        fullWidth
                        helperText="Streckkod för läkemedlet"
                    />

                    <TextField
                        label="GTIN"
                        value={gtin}
                        onChange={(e) => setGtin(e.target.value)}
                        fullWidth
                        helperText="GTIN (PC)"
                    />

                    <Alert severity="info" sx={{ mt: 1 }}>
                        Minst en av streckkod eller GTIN måste anges
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Avbryt
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || isLoading}
                >
                    {saving ? 'Sparar...' : 'Spara'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SynonymDialog;
